import React from 'react';
import { AlertTriangle, ArrowRight, TrendingUp } from 'lucide-react';
import { getMaturityCriteria, PRODUCT_SUGGESTIONS } from '../../config/cisaMaturityModel';

/**
 * GapCard - Displays a single gap item with priority indicator and remediation info
 *
 * Props:
 * - functionCode: string (e.g., 'ID_AUTH')
 * - functionName: string (e.g., 'Authentication')
 * - pillarName: string (e.g., 'Identity')
 * - pillarColor: string (hex color)
 * - currentLevel: string ('TRADITIONAL' | 'INITIAL' | 'ADVANCED' | 'OPTIMAL')
 * - targetLevel: string
 * - onViewDetails: () => void (optional)
 */
const GapCard = ({
  functionCode,
  functionName,
  pillarName,
  pillarColor,
  currentLevel,
  targetLevel,
  onViewDetails
}) => {
  const LEVEL_ORDER = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
  const currentIndex = LEVEL_ORDER.indexOf(currentLevel || 'TRADITIONAL');
  const targetIndex = LEVEL_ORDER.indexOf(targetLevel || 'TRADITIONAL');
  const gapLevels = targetIndex - currentIndex;

  // Determine priority based on gap size
  const getPriority = (gap) => {
    if (gap >= 3) return { label: 'Critical', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
    if (gap >= 2) return { label: 'High', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' };
    return { label: 'Medium', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' };
  };

  const priority = getPriority(gapLevels);

  // Get next level to achieve
  const nextLevel = LEVEL_ORDER[currentIndex + 1];
  const nextLevelCriteria = nextLevel ? getMaturityCriteria(functionCode, nextLevel) : null;
  const nextLevelProducts = nextLevel ? (PRODUCT_SUGGESTIONS[functionCode]?.[nextLevel] || []) : [];

  // Format level name
  const formatLevel = (level) => level.charAt(0) + level.slice(1).toLowerCase();

  return (
    <div className="cisa-gap-card" style={{ '--gap-card-color': pillarColor }}>
      <div className="cisa-gap-card-header">
        <div className="cisa-gap-card-info">
          <span
            className="cisa-gap-card-pillar-badge"
            style={{ background: pillarColor }}
          >
            {pillarName.charAt(0)}
          </span>
          <div className="cisa-gap-card-title">
            <span className="cisa-gap-card-function">{functionName}</span>
            <span className="cisa-gap-card-pillar">{pillarName}</span>
          </div>
        </div>

        <div className="cisa-gap-card-priority" style={{ background: priority.bg, color: priority.color }}>
          <AlertTriangle size={12} />
          {priority.label}
        </div>
      </div>

      <div className="cisa-gap-card-levels">
        <div className="cisa-gap-card-level cisa-gap-card-level--current">
          <span className="cisa-gap-card-level-label">Current</span>
          <span
            className={`cisa-level-badge cisa-level--${currentLevel?.toLowerCase()}`}
          >
            {formatLevel(currentLevel || 'TRADITIONAL')}
          </span>
        </div>

        <ArrowRight size={16} className="cisa-gap-card-arrow" />

        <div className="cisa-gap-card-level cisa-gap-card-level--target">
          <span className="cisa-gap-card-level-label">Target</span>
          <span
            className={`cisa-level-badge cisa-level--${targetLevel?.toLowerCase()}`}
          >
            {formatLevel(targetLevel || 'TRADITIONAL')}
          </span>
        </div>

        <div className="cisa-gap-card-gap-badge">
          <TrendingUp size={12} />
          {gapLevels} level{gapLevels > 1 ? 's' : ''} to go
        </div>
      </div>

      {/* Next step preview */}
      {nextLevel && (
        <div className="cisa-gap-card-next">
          <div className="cisa-gap-card-next-header">
            <span className="cisa-gap-card-next-label">Next Step: Reach {formatLevel(nextLevel)}</span>
          </div>

          {nextLevelCriteria && (
            <p className="cisa-gap-card-next-criteria">{nextLevelCriteria}</p>
          )}

          {nextLevelProducts.length > 0 && (
            <div className="cisa-gap-card-next-products">
              <span className="cisa-gap-card-products-label">Suggested:</span>
              <div className="cisa-gap-card-products-list">
                {nextLevelProducts.slice(0, 3).map((product, idx) => (
                  <span key={idx} className="cisa-gap-card-product-tag">
                    {product}
                  </span>
                ))}
                {nextLevelProducts.length > 3 && (
                  <span className="cisa-gap-card-product-more">
                    +{nextLevelProducts.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {onViewDetails && (
        <button
          type="button"
          className="cisa-gap-card-view-btn"
          onClick={onViewDetails}
        >
          View Details
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
};

export default GapCard;
